import Vue from 'vue'
import { reassociateDeviceSubscription, unsubscribeFromPush } from '@/utils/pushNotifications'
import { collectDeviceInfo } from '@/utils/deviceInfo'

const app = new Vue()

export default {
  namespaced: true,
  state: {
    userError: '',
    userLoading: false,
    emailSent: false,
    emailSendingError: '',
    setNewPasswordLoading: false,
    passwordSent: false,
    setNewPasswordError: '',
    token: null
  },
  getters: {
    isAuthorised(state) {
      return !!state.token
    }
  },
  mutations: {
    USER_LOADING(state) {
      state.userError = ''
      state.userLoading = true
    },
    USER_LOADED(state) {
      state.userLoading = false
    },
    USER_ERROR(state, error) {
      state.userLoading = false
      state.userError = error
    },
    USER_CLEAR_ERROR(state) {
      state.userError = ''
    },
    SET_USER_TOKEN(state, token) {
      state.token = token
    }
  },
  actions: {
    /**
     * Fire-and-forget device/session capture for a fresh sign-in.
     * Must never block or break the sign-in flow.
     */
    captureSignIn() {
      try {
        app.$api.sessions.capture(collectDeviceInfo()).catch(() => {})
      } catch (e) {
        // ignore
      }
    },
    /**
     * Fire-and-forget push subscription re-association (P4): on a shared device
     * the existing subscription must start delivering to the account that just
     * signed in. Never prompts for permission.
     */
    reassociateDevice() {
      try {
        reassociateDeviceSubscription(app.$api).catch(() => {})
      } catch (e) {
        // ignore
      }
    },
    async resetPassword({}, payload) {
      try {
        await app.$api.auth.resetPassword(payload)
      } catch (error) {
        throw error
        // commit('USER_ERROR', app.$getErrorMessage(error))
      }
    },
    async resetPasswordComplete({}, payload) {
      try {
        return await app.$api.auth.resetPasswordComplete(payload)
      } catch (error) {
        throw error
        // commit('USER_ERROR', app.$getErrorMessage(error))
      }
    },
    async signIn({ commit, dispatch }, payload) {
      commit('USER_LOADING')
      try {
        const response = await app.$api.auth.login(payload)
        const { token } = response
        commit('SET_USER_TOKEN', token)
        commit('USER_LOADED')
        await dispatch('getAppData', {}, { root: true })
        await dispatch('user/fetchUser', {}, { root: true })
        dispatch('captureSignIn')
        dispatch('reassociateDevice')
      } catch (error) {
        throw error
        // commit('USER_ERROR', app.$getErrorMessage(error))
      }
    },
    async setToken({ commit, dispatch }, { token }) {
      commit('USER_LOADING')
      try {
        commit('SET_USER_TOKEN', token)
        commit('USER_LOADED')
        await dispatch('getAppData', {}, { root: true })
        await dispatch('user/fetchUser', {}, { root: true })
        dispatch('captureSignIn')
        dispatch('reassociateDevice')
      } catch (error) {
        throw error
        // commit('USER_ERROR', app.$getErrorMessage(error))
      }
    },
    async invite({ commit, dispatch }, payload) {
      commit('USER_LOADING')
      try {
        const response = await app.$api.auth.invite(payload)
        const { token } = response
        commit('SET_USER_TOKEN', token)
        commit('USER_LOADED')
        await dispatch('getAppData', {}, { root: true })
        await dispatch('user/fetchUser', {}, { root: true })
        dispatch('captureSignIn')
        dispatch('reassociateDevice')
      } catch (error) {
        // commit('USER_ERROR', app.$getErrorMessage(error))
        throw error
      }
    },
    async logOut({ commit, dispatch }, { preservePendingRoute = false } = {}) {
      // Unsubscribe from push notifications. This MUST complete before the auth
      // token is cleared (DELETE /push/unsubscribe is authenticated) and before
      // the page unloads — callers (LogoutPage, AuthAccessor) await this action.
      try {
        await unsubscribeFromPush(app.$api)
      } catch (err) {
        console.error('Push unsubscribe failed:', err)
      }

      // NOTE (P1): `notificationPromptShown` ("Not now") and `iosInstallPromptDismissed`
      // intentionally SURVIVE logout — once the user declined, the prompt must not
      // reappear on the next sign-in. (The device is unsubscribed anyway; the next
      // sign-in with granted permission re-subscribes silently via P4.)

      // Hygiene (P5): a pending deep-link is one-shot — it is consumed on the next
      // sign-in. A manual logout should not resurrect it. AuthAccessor (re-entry
      // via /auth/:token after the external login) passes preservePendingRoute so
      // the pending route survives the logOut that precedes setToken.
      if (!preservePendingRoute) {
        try {
          window.localStorage.removeItem('pendingRouteAfterAuth')
        } catch (e) {
          // ignore storage errors
        }
      }

      commit('SET_USER_TOKEN', null)
      dispatch('user/reset', {}, { root: true })
      dispatch('study/reset', {}, { root: true })
      dispatch('chats/reset', {}, { root: true })
      dispatch('studies/reset', {}, { root: true })
      dispatch('fileUpload/reset', {}, { root: true })
      dispatch('completeRegistration/reset', {}, { root: true })
    },
    async checkInvitationToken({}, { email, token }) {
      let response = await app.$api.auth.info({
        email,
        invitationLink: token
      })
      return {
        studyId: response.studyId,
        isPasswordSet: response.isPasswordSet
      }
    }
  }
}
